import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Dropdown, DropdownCard, DropdownContent } from 'shared/ui'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { IParamsContactsOptOut } from 'entities/Contacts/api/types'
import { successOpIinHandler } from 'entities/Contacts/lib/successOpIinHandler'
import { useBlockContactModal } from 'entities/Contacts/ui/BlockContactModal'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { WorkflowEnrollDropdownItem } from 'widgets/WorkflowEnroll'
import { ContactEnrollDropdownItem } from 'widgets/ChatbotEnroll'
import { useContactsProfileCardContext } from 'widgets/ContactsDetails/ui/ContactsProfileCard/context'
import { ContactsOptInModalStore } from 'widgets/ContactsOptInModal'
import styles from './styles.module.scss'

type IContactActionsProps = {
  contact: Contact
}

export const ContactActions: FC<IContactActionsProps> = observer(({ contact }) => {
  const entities = useEntitiesContext()
  const { eventLogStore, organizationStore } = entities
  const store = useContactsProfileCardContext()

  const { handleBlock } = useBlockContactModal(contact, {
    resetActiveAndCloseDetailsIsMobile: store.config?.resetActiveAndCloseDetailsIsMobile,
  })

  const handleOptIn = () => {
    store.config?.resetActiveAndCloseDetailsIsMobile?.()

    if (store.config?.handleOptIn) {
      store.config?.handleOptIn(contact)

      return
    }

    new ContactsOptInModalStore(entities).handleOpen([contact], {
      successHandler: (opt_outs: IParamsContactsOptOut['opt_outs']) => {
        successOpIinHandler(contact, opt_outs)
      },
      isBulkAll: false,
    })
  }

  const handleConversationsHistory = () => {
    const payload = {
      event_id: 'unified_history_opened',
    }

    eventLogStore.logEvent('Unified History Opened', payload, { groupId: organizationStore.id })
    store.openView?.('conversations-history')
  }

  const disabled = contact.id === 0 || contact.id === -1 || !contact
  const isUnknowContact = contact.isUnknownContact

  return (
    <div className={styles.modalWrapper}>
      <Dropdown
        ariaLabel={'ContactActions'}
        disabled={disabled}
        icon={'moreHorizontal'}
        placement={'bottom-end'}
        tooltipProps={{
          label: 'Show actions',
          placement: 'top-end',
          disableInteractive: true,
        }}
        sizeTriggerButton='medium'
        customComponent={(onCloseMenu) => (
          <DropdownContent>
            <WorkflowEnrollDropdownItem contact={contact} onClick={onCloseMenu} />
            <ContactEnrollDropdownItem contact={contact} onClick={onCloseMenu} />
            <DropdownCard
              item={{
                id: 'conversationsHistory',
                label: 'Conversations history',
                iconL: 'clockBackward',
                ariaLabel: 'conversationsHistorySelector',
              }}
              onChange={() => {
                onCloseMenu()
                handleConversationsHistory()
              }}
            />
            <DropdownCard
              item={{
                id: 'optInStatus',
                label: 'Opt-in status',
                iconL: 'shield',
                ariaLabel: 'optInStatusSelector',
              }}
              onChange={() => {
                onCloseMenu()
                handleOptIn()
              }}
            />
            {isUnknowContact && (
              <DropdownCard
                item={{
                  id: 'block',
                  label: contact.is_blocked ? 'Unblock' : 'Block and report spam',
                  iconL: 'phoneOff',
                  ariaLabel: 'blockSelector',
                  ...(!contact.is_blocked && {
                    customFields: {
                      leftIconColor: 'var(--content-destructive-primary)',
                      titleColor: 'var(--content-destructive-primary)',
                    },
                  }),
                }}
                onChange={() => {
                  onCloseMenu()
                  handleBlock()
                }}
              />
            )}
            {!isUnknowContact && (
              <DropdownCard
                item={{
                  id: 'block',
                  label: contact.is_blocked ? 'Unblock' : 'Block',
                  iconL: 'block',
                  ariaLabel: 'blockSelector',
                }}
                onChange={() => {
                  onCloseMenu()
                  handleBlock({ reportSpam: false })
                }}
              />
            )}
          </DropdownContent>
        )}
      />
    </div>
  )
})
